import React, { useEffect, useState } from 'react';
import { X, Minus, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { useQuote } from './Quote/QuoteContext';
import { useUserScheme } from './UserSchemeContext';
import { auth, db } from '../utils/firebase';
import { doc, getDoc } from 'firebase/firestore';

export default function CartModal({ isOpen, onClose, cart, setCart }) {
    const { openQuote } = useQuote();
    const contextSchemeId = useUserScheme();
    const [schemeId, setSchemeId] = useState(null);
    const [showEasyOrderInput, setShowEasyOrderInput] = useState(false);
    const [easyOrderListName, setEasyOrderListName] = useState('');
    const [existingEasyOrders, setExistingEasyOrders] = useState([]);
    const [selectedEasyOrderId, setSelectedEasyOrderId] = useState('new');
    const [isSavingEasyOrder, setIsSavingEasyOrder] = useState(false);

    const fetchExistingEasyOrders = async () => {
        if (!auth.currentUser) return;
        try {
            const { collection: _collection, getDocs: _getDocs } = await import('firebase/firestore');
            const easyOrdersRef = _collection(db, "users", auth.currentUser.uid, "easy_orders");
            const snap = await _getDocs(easyOrdersRef);
            const list = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setExistingEasyOrders(list);
        } catch (e) {
            console.error("Error loading existing easy orders:", e);
        }
    };

    const handleSaveEasyOrder = async () => {
        if (!auth.currentUser) {
            toast.error("Please sign in to save templates.");
            return;
        }

        setIsSavingEasyOrder(true);
        try {
            const uid = auth.currentUser.uid;
            const { collection: _collection, addDoc: _addDoc, doc: _doc, getDoc: _getDoc, updateDoc: _updateDoc } = await import('firebase/firestore');

            const itemsToSave = cart.map(item => ({
                product_id: item.product.id || item.product.objectID || '',
                quantity: item.quantity,
                title: item.product.title || '',
                image: item.product.image || '/logo.png',
                code: item.product.code || '',
                price: item.product.price || item.product.distributorPrice || 0
            }));

            if (selectedEasyOrderId === 'new') {
                if (!easyOrderListName.trim()) {
                    toast.error("Please enter a name for your easy order template.");
                    setIsSavingEasyOrder(false);
                    return;
                }

                const easyOrdersRef = _collection(db, "users", uid, "easy_orders");
                await _addDoc(easyOrdersRef, {
                    name: easyOrderListName.trim(),
                    items: itemsToSave,
                    createdAt: new Date().toISOString()
                });

                toast.success("Easy Order template created successfully!");
            } else {
                const targetDocRef = _doc(db, "users", uid, "easy_orders", selectedEasyOrderId);
                const targetSnap = await _getDoc(targetDocRef);

                if (!targetSnap.exists()) {
                    toast.error("Selected list no longer exists.");
                    setIsSavingEasyOrder(false);
                    return;
                }

                const targetData = targetSnap.data();
                let currentItems = [...(targetData.items || [])];

                itemsToSave.forEach(newItem => {
                    const existingIdx = currentItems.findIndex(ci => 
                        (newItem.product_id && ci.product_id === newItem.product_id) ||
                        (newItem.code && ci.code === newItem.code) ||
                        (newItem.title && ci.title === newItem.title)
                    );
                    if (existingIdx > -1) {
                        currentItems[existingIdx] = {
                            ...currentItems[existingIdx],
                            quantity: Number(currentItems[existingIdx].quantity || 0) + Number(newItem.quantity || 0)
                        };
                    } else {
                        currentItems.push(newItem);
                    }
                });

                await _updateDoc(targetDocRef, {
                    items: currentItems,
                    updatedAt: new Date().toISOString()
                });

                toast.success(`Updated "${targetData.name}" template with cart items!`);
            }

            setShowEasyOrderInput(false);
            setEasyOrderListName('');
            setSelectedEasyOrderId('new');
        } catch (e) {
            console.error("Error saving easy order template:", e);
            toast.error("Failed to save template. Please try again.");
        } finally {
            setIsSavingEasyOrder(false);
        }
    };

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged(async (user) => {
            if (user) {
                try {
                    let userDocRef = doc(db, "users", user.uid);
                    let userSnap = await getDoc(userDocRef);
                    if (!userSnap.exists()) {
                        userDocRef = doc(db, "users", user.email.toLowerCase());
                        userSnap = await getDoc(userDocRef);
                    }
                    if (userSnap.exists()) {
                        const userData = userSnap.data();
                        const sId = userData.scheme_id || userData.schema_id || null;
                        setSchemeId(sId);
                    } else {
                        setSchemeId(null);
                    }
                } catch (e) {
                    console.error("Error loading user scheme_id", e);
                    setSchemeId(null);
                }
            } else {
                setSchemeId(null);
            }
        });
        return () => unsubscribe();
    }, []);

    const activeSchemeId = schemeId || contextSchemeId;

    const findSchemePricing = (product, sId = activeSchemeId) => {
        if (!product || !sId) return null;

        if (product.pricing && Array.isArray(product.pricing)) {
            const direct = product.pricing.find(p =>
                p && String(p.scheme_id || p.schema_id || p.schemeId || '') === String(sId)
            );
            if (direct) return direct;
        }

        if (product.schemeIds && Array.isArray(product.schemeIds) && product.pricing && Array.isArray(product.pricing)) {
            const idx = product.schemeIds.findIndex(id => String(id) === String(sId));
            if (idx !== -1 && product.pricing[idx]) {
                return product.pricing[idx];
            }
        }

        return null;
    };

    const getProductPriceForQty = (product, qty) => {
        const schemePricing = findSchemePricing(product);

        if (schemePricing && schemePricing.tiers && schemePricing.tiers.length > 0) {
            const ascTiers = [...schemePricing.tiers].sort((a, b) => {
                const qa = Number(a.min ?? a.min_qty ?? a.minQty ?? 0);
                const qb = Number(b.min ?? b.min_qty ?? b.minQty ?? 0);
                return qa - qb;
            });

            const matched = [...ascTiers].reverse().find(t => qty >= Number(t.min ?? t.min_qty ?? t.minQty ?? 0));
            if (matched && matched.price !== undefined && matched.price !== null && Number(matched.price) > 0) {
                return Number(matched.price);
            }
            if (ascTiers[0] && ascTiers[0].price !== undefined && ascTiers[0].price !== null && Number(ascTiers[0].price) > 0) {
                return Number(ascTiers[0].price);
            }
        }

        if (product.price !== undefined && product.price !== null && Number(product.price) > 0) {
            return Number(product.price);
        }

        return 0;
    };

    const getActiveProductTier = (product, qty) => {
        const schemePricing = findSchemePricing(product);
        if (!schemePricing || !schemePricing.tiers || schemePricing.tiers.length === 0) return null;

        const ascTiers = [...schemePricing.tiers].sort((a, b) => {
            const qa = Number(a.min ?? a.min_qty ?? a.minQty ?? 0);
            const qb = Number(b.min ?? b.min_qty ?? b.minQty ?? 0);
            return qa - qb;
        });

        const matched = [...ascTiers].reverse().find(t => qty >= Number(t.min ?? t.min_qty ?? t.minQty ?? 0));
        return matched || ascTiers[0];
    };

    if (!isOpen) return null;
    // Calculate total item count (sum of quantities)
    const totalItems = cart.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);

    // Whether ANY item in the cart has insufficient stock
    const hasInsufficientStockItems = cart.some(item => 
        item.product.stock !== undefined && 
        (item.quantity > item.product.stock || item.product.stock < (item.product.minimum_order || 1))
    );

    // Calculate subtotal dynamically using active scheme tier prices
    const subtotal = cart.reduce((sum, item) => {
        const price = getProductPriceForQty(item.product, Number(item.quantity) || 0);
        return sum + (price * (Number(item.quantity) || 0));
    }, 0);


    const getProductCode = (p) => {
        if (!p) return '';
        if (p.code) return p.code;
        if (p.productCodeLetters && p.productCode) return `${p.productCodeLetters}${p.productCode}`;
        return p.productCode || '';
    };

    const getProductTitle = (p) => {
        if (!p) return '';
        return p.title || p.productName || '';
    };

    const getProductId = (p) => {
        if (!p) return '';
        return p.id || p.objectID || '';
    };

    const areProductsEqual = (p1, p2) => {
        const id1 = getProductId(p1);
        const id2 = getProductId(p2);
        if (id1 && id2) return id1 === id2;

        const code1 = getProductCode(p1);
        const code2 = getProductCode(p2);
        if (code1 && code2) return code1 === code2;

        const title1 = getProductTitle(p1);
        const title2 = getProductTitle(p2);
        return !!(title1 && title2 && title1 === title2);
    };

    const updateQuantity = (product, newQty) => {
        const minimum_order = product.minimum_order || 1;
        if (newQty < minimum_order) {
            toast.error(`Minimum order quantity is ${minimum_order}`);
            return;
        }
        if (product.stock !== undefined && newQty > product.stock) {
            toast.error("Out of stock");
        }
        setCart(prev => prev.map(item => {
            const match = areProductsEqual(item.product, product);
            return match ? { ...item, quantity: newQty } : item;
        }));
    };

    const handleQtyChange = (prod, e) => {
        const val = e.target.value;
        if (val === '') {
            setCart(prev => prev.map(item => {
                const match = areProductsEqual(item.product, prod);
                return match ? { ...item, quantity: '' } : item;
            }));
        } else {
            const parsed = parseInt(val, 10);
            if (!isNaN(parsed) && parsed >= 1) {
                if (prod.stock !== undefined && parsed > prod.stock) {
                    toast.error("Out of stock");
                }
                setCart(prev => prev.map(item => {
                    const match = areProductsEqual(item.product, prod);
                    return match ? { ...item, quantity: parsed } : item;
                }));
            }
        }
    };

    const handleQtyBlur = (prod, currentItem) => {
        const minimum_order = prod.minimum_order || 1;
        if (currentItem.quantity === '' || currentItem.quantity < minimum_order) {
            toast.error(`Minimum order quantity is ${minimum_order}`);
            setCart(prev => prev.map(item => {
                const match = areProductsEqual(item.product, prod);
                return match ? { ...item, quantity: minimum_order } : item;
            }));
        }
    };

    const removeItem = (product) => {
        setCart(prev => prev.filter(item => {
            const match = areProductsEqual(item.product, product);
            return !match;
        }));
        toast.success(`Removed ${product.title || product.productName || 'product'} from cart`);
    };

    return (
        <div className="chefco-cart-overlay" onClick={onClose}>
            <div className="chefco-cart-modal" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="chefco-cart-header">
                    <h3 className="chefco-cart-title">
                        My Cart ({totalItems} {totalItems === 1 ? 'Item' : 'Items'})
                    </h3>
                    <button onClick={onClose} className="chefco-cart-close hover:shadow-md hover:-translate-y-0.5 transition-all" aria-label="Close cart">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="chefco-cart-body scrollbar-thin">
                    {cart.length > 0 ? (
                        <div className="space-y-0.5">
                            {cart.map((item, idx) => {
                                const prod = item.product;
                                const sku = prod.code || "NEW";
                                const itemPrice = getProductPriceForQty(prod, Number(item.quantity) || 0);
                                return (
                                    <div key={idx} className="chefco-cart-item animate-in fade-in duration-150 flex flex-col md:flex-row md:items-center gap-4">
                                        <div className="chefco-cart-item-left flex-1">
                                            <div className="chefco-cart-item-img-wrapper">
                                                <img
                                                    src={prod.image}
                                                    alt={prod.title}
                                                    className="chefco-cart-item-img"
                                                />
                                            </div>
                                            <div className="chefco-cart-item-info">
                                                <h4 className="chefco-cart-item-name text-ellipsis overflow-hidden">
                                                    {prod.title} ({sku})
                                                </h4>
                                                <span className="chefco-cart-item-meta">
                                                    SKU: {sku}
                                                </span>
                                                <span className="chefco-cart-item-meta text-[#9E2A2B] font-extrabold mt-1">
                                                    ₹{itemPrice.toFixed(2)} each
                                                </span>
                                                {prod.taxApplicable && (
                                                    <span className="inline-flex items-center text-[9px] font-black uppercase tracking-wide bg-amber-50 text-amber-600 border border-amber-200 px-1.5 py-0.5 rounded-md mt-1">
                                                        +Tax Applicable
                                                    </span>
                                                )}
                                                {prod.stock !== undefined && prod.stock < (prod.minimum_order || 1) ? (
                                                    <span className="inline-flex items-center text-[9px] font-black uppercase tracking-wide bg-red-50 text-red-600 border border-red-200 px-1.5 py-0.5 rounded-md mt-1.5">
                                                        Out of Stock
                                                    </span>
                                                ) : prod.stock !== undefined && item.quantity > prod.stock ? (
                                                    <span className="inline-flex items-center text-[9px] font-black uppercase tracking-wide bg-amber-50 text-amber-600 border border-amber-200 px-1.5 py-0.5 rounded-md mt-1.5 font-medium">
                                                        Insufficient Stock (Available: {prod.stock})
                                                    </span>
                                                ) : null}

                                                {/* Tier options buttons */}
                                                {findSchemePricing(prod)?.tiers?.length > 0 && (
                                                    <div className="mt-2 flex flex-col gap-1 text-left">
                                                        <span className="text-[9.5px] font-black text-slate-400 uppercase tracking-wider">Volume Pricing Tiers:</span>
                                                        <div className="flex gap-1.5 flex-wrap">
                                                            {findSchemePricing(prod).tiers.map((t, tIdx) => {
                                                                const tMin = Number(t.min || t.min_qty || t.minQty || 0);
                                                                const activePrice = Number(t.price || 0);
                                                                const activeTier = getActiveProductTier(prod, item.quantity);
                                                                const isActive = activeTier === t;

                                                                return (
                                                                    <button
                                                                        key={tIdx}
                                                                        onClick={() => updateQuantity(prod, tMin)}
                                                                        className={`px-2 py-0.5 rounded text-[9.5px] font-black border transition cursor-pointer ${isActive
                                                                            ? 'bg-[#2C3E2B] text-white border-[#2C3E2B] shadow-xs'
                                                                            : 'bg-white text-slate-500 border-slate-200 hover:border-slate-400'
                                                                            }`}
                                                                    >
                                                                        {tMin}+ (₹{activePrice.toFixed(2)})
                                                                    </button>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <div className="chefco-cart-item-right flex flex-row md:flex-col items-center md:items-end justify-between md:justify-center gap-2">
                                            <div className="text-right flex flex-col items-end">
                                                <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Item Total</span>
                                                <span className="text-sm font-extrabold text-[#2C3E2B]">
                                                    ₹{(itemPrice * (Number(item.quantity) || 0)).toFixed(2)}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <div className="chefco-cart-qty-selector">
                                                    <button
                                                        onClick={() => updateQuantity(prod, item.quantity - 1)}
                                                        className="chefco-cart-qty-btn"
                                                        aria-label="Decrease quantity"
                                                    >
                                                        <Minus className="w-3 h-3" />
                                                    </button>
                                                    <input
                                                        type="text"
                                                        value={item.quantity}
                                                        onChange={(e) => handleQtyChange(prod, e)}
                                                        onBlur={() => handleQtyBlur(prod, item)}
                                                        className="w-8 text-center text-xs font-extrabold text-slate-700 bg-transparent border-none outline-none focus:ring-0 focus:outline-none p-0"
                                                    />
                                                    <button
                                                        onClick={() => updateQuantity(prod, item.quantity + 1)}
                                                        className="chefco-cart-qty-btn"
                                                        aria-label="Increase quantity"
                                                    >
                                                        <Plus className="w-3 h-3" />
                                                    </button>
                                                </div>
                                                <button
                                                    onClick={() => removeItem(prod)}
                                                    className="chefco-cart-remove-btn"
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="py-12 text-center">
                            <span className="text-4xl">🛒</span>
                            <p className="text-slate-500 font-bold mt-3 text-sm">Your cart is currently empty.</p>
                            <p className="text-xs text-slate-400 mt-1">Add items from the products list to get started!</p>
                        </div>
                    )}
                </div>

                {/* Summary */}
                {cart.length > 0 && (
                    <div className="chefco-cart-summary">
                        <div className="chefco-cart-summary-row flex justify-between gap-4">
                            <span className="chefco-cart-summary-label shrink-0">Subtotal:</span>
                            <span className="chefco-cart-summary-value break-all text-right">₹{subtotal.toFixed(2)}</span>
                        </div>
                        <div className="chefco-cart-summary-row text-lg flex justify-between gap-4">
                            <span className="chefco-cart-summary-label shrink-0">Total:</span>
                            <span className="chefco-cart-summary-value break-all text-right">₹{subtotal.toFixed(2)}</span>
                        </div>
                    </div>
                )}

                {cart.length > 0 && auth.currentUser && (
                    <div className="px-6 pb-4 border-b border-slate-100 flex flex-col gap-2">
                        {!showEasyOrderInput ? (
                            <button
                                onClick={() => {
                                    setShowEasyOrderInput(true);
                                    fetchExistingEasyOrders();
                                }}
                                className="text-xs text-[#2C3E2B] font-extrabold hover:text-[#9E2A2B] bg-transparent border-none cursor-pointer flex items-center justify-center gap-1.5 py-1.5 transition-colors"
                            >
                                <span>💾 Save cart list as Easy Order template</span>
                            </button>
                        ) : (
                            <div className="flex flex-col gap-2.5 p-3 bg-slate-50 rounded-xl border border-slate-200 text-left">
                                <span className="text-[10px] font-black text-slate-500 uppercase tracking-wider block">Save to Easy Order List</span>
                                
                                {existingEasyOrders.length > 0 && (
                                    <div className="flex flex-col gap-1">
                                        <label className="text-[11px] font-bold text-slate-600">Select List</label>
                                        <select
                                            value={selectedEasyOrderId}
                                            onChange={(e) => setSelectedEasyOrderId(e.target.value)}
                                            className="px-3 py-1.5 text-xs border border-slate-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#2C3E2B] bg-white text-slate-800 font-semibold"
                                        >
                                            <option value="new">+ Create New Easy Order List</option>
                                            {existingEasyOrders.map((list) => (
                                                <option key={list.id} value={list.id}>
                                                    {list.name} ({list.items?.length || 0} items)
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}

                                {selectedEasyOrderId === 'new' && (
                                    <div className="flex flex-col gap-1">
                                        <label className="text-[11px] font-bold text-slate-600">Template Name</label>
                                        <input
                                            type="text"
                                            placeholder="e.g. Weekly Kitchen Order"
                                            value={easyOrderListName}
                                            onChange={(e) => setEasyOrderListName(e.target.value)}
                                            className="w-full px-3 py-1.5 text-xs border border-slate-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#2C3E2B] bg-white text-slate-800"
                                        />
                                    </div>
                                )}

                                <div className="flex gap-2 pt-1">
                                    <button
                                        onClick={handleSaveEasyOrder}
                                        disabled={isSavingEasyOrder}
                                        className="px-3.5 py-1.5 bg-[#2C3E2B] text-white text-xs font-black rounded-lg border-none cursor-pointer disabled:opacity-50 hover:bg-[#1e2d1e] transition"
                                    >
                                        {isSavingEasyOrder ? "Saving..." : selectedEasyOrderId === 'new' ? "Create & Save" : "Add to List"}
                                    </button>
                                    <button
                                        onClick={() => {
                                            setShowEasyOrderInput(false);
                                            setEasyOrderListName('');
                                            setSelectedEasyOrderId('new');
                                        }}
                                        className="px-2.5 py-1.5 bg-slate-200 text-slate-600 text-xs font-black rounded-lg border-none cursor-pointer hover:bg-slate-300 transition"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <div className="chefco-cart-footer">
                    {cart.length > 0 && (
                        schemeId ? (
                            <Link to={hasInsufficientStockItems ? '#' : '/checkout'}>
                                <button
                                    disabled={hasInsufficientStockItems}
                                    onClick={hasInsufficientStockItems ? undefined : onClose}
                                    className={`chefco-cart-btn-primary hover:shadow-md hover:-translate-y-0.5 transition-all ${hasInsufficientStockItems ? 'opacity-50 cursor-not-allowed bg-slate-400' : ''}`}
                                >
                                    Proceed to Checkout
                                </button>
                            </Link>
                        ) : (
                            <button
                                onClick={() => {
                                    if (!auth.currentUser) {
                                        toast.error("Please sign in to request a quote.");
                                        window.dispatchEvent(new Event('open-auth'));
                                        return;
                                    }
                                    openQuote({
                                        cart: true,
                                        products: cart.map(item => ({
                                            productName: item.product.productName || item.product.title || "",
                                            category: item.product.categoryName || item.product.brandName || "N/A",
                                            cart_quantity: item.quantity,
                                            productId: item.product.id || item.product.objectID || ""
                                        }))
                                    });
                                    onClose();
                                }}
                                className="flex items-center justify-center gap-1 bg-[#C3D8C3] border border-[#547454] hover:border-[#395639] text-[#395639] hover:text-[#2C3E2B] font-medium cursor-pointer transition-colors duration-200 text-xs w-full py-3 px-3 rounded-lg"
                            >
                                Get Quote
                            </button>
                        )
                    )}

                    <button onClick={onClose} className="chefco-cart-btn-secondary hover:shadow-md hover:-translate-y-0.5 transition-all">
                        Continue Shopping
                    </button>
                </div>
            </div>
        </div>
    );
}
